// Ported from dev-scrips-pm-react src/billing/components/claim-review/info-grid.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { format as formatDate, isValid as isDateValid, parseISO } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { Skeleton } from '@/billing/bridge';
import { isSelfPay as isSelfPayType } from '@/billing/lib/sponsor-type';
import type { InvoiceDetail } from '@/shared/types/billing';
import { formatMrnLabel } from '@/shared/lib/mrn';

/**
 * Three-column summary at the top of the Claim Review popup.
 *
 * Flutter parity: `review_claim_popup.dart:3276-3384` (`ClaimUserInfo`).
 * Column 1: Patient name + MRN + Age/Gender + Address
 * Column 2: Encounter Date + Invoice # + Invoice Date + Claim ID + Claim Date + Provider + Encounter #
 * Column 3: Claim Status + Sponsor Type + Insurance + Member ID + Eligibility Start + Expiration + Phone #
 *
 * **Age/Gender + Address + Phone**: read straight off `invoice.patient`
 * (`InvoiceDetailPatient`). The `GET /Billing/Invoice/{id}` response already
 * hydrates the full patient record (age, genderName, fullAddress, phoneNumber)
 * — verified live 2026-06-02/03 — so no separate `GET /PatientModule/Patient/{id}`
 * is needed (Flutter fetches it separately, but the invoice carries it). 969c.
 */
interface Props {
  invoice: InvoiceDetail;
  isLoading?: boolean;
  /**
   * `claim` (default) — claim-review columns (Claim ID/Date/Status, Insurance,
   * Member ID, Eligibility). `invoice` — appointment / patient billing tab
   * (Flutter `ProfileInfoWidget` / `invoice_billing_screen`): column 2 drops
   * the Claim ID/Date rows; column 3 shows Invoice Status + Sponsor, and the
   * Insurance/Member-ID/Eligibility/Phone rows only when NOT Self-pay.
   */
  variant?: 'claim' | 'invoice';
  /**
   * When provided, the external-link icon beside the patient name becomes a
   * button that opens the patient profile. Flutter parity:
   * `review_claim_popup.dart:3298-3303` (`fieldValue('Patient', …,
   * showIcon: true)`) → the icon's InkWell fires `openPatientScreen`
   * (`Navigator.push(CreatePatientBaseScreen)`). Omit to keep the icon
   * decorative (e.g. invoice-detail / receipt surfaces that don't navigate).
   */
  onViewPatient?: () => void;
}

export function ClaimReviewInfoGrid({
  invoice,
  isLoading = false,
  variant = 'claim',
  onViewPatient,
}: Props) {
  if (isLoading) return <InfoGridSkeleton />;
  const isInvoice = variant === 'invoice';
  const isSelfPay = isSelfPayType(invoice.sponsorType);

  const patient = invoice.patient;
  const claim = invoice.claim;
  // `InvoiceDetail.sponsor` is typed as `InvoiceSponsorRef` — includes
  // `payerName`, `phoneNumber`, `address`, etc. from the hydrated PayerDetails
  // shape the wire ships. No cast needed.
  const sponsor = invoice.sponsor;
  const provider = invoice.provider;

  // MRN may already be prefixed "MRN ..." by the backend; the shared label never doubles it.
  const mrnDisplay = formatMrnLabel(patient?.mrn) ?? '—';

  // Age / Gender + Address — straight off the hydrated invoice patient.
  const ageGender = [patient?.age, patient?.genderName].filter(Boolean).join(' / ') || '—';
  const patientAddress = patient?.fullAddress?.trim() || patient?.address?.trim() || '—';
  // Phone # lives in the INSURANCE/payer column, so it binds the SPONSOR's phone
  // — NOT the patient's. Binding patient.phoneNumber here showed the wrong number
  // and leaked patient PHI into the insurance section (PROD-1753). Flutter parity:
  // `review_claim_popup.dart:3376` — `fieldValue('Phone #', invoice?.sponsor?.phoneNumber ?? emptyLabel)`.
  // Many records ship sponsor.phoneNumber as '' → legitimately renders '—'.
  const payerPhone = sponsor?.phoneNumber?.trim() || '—';

  // Currency-less primitive fields — all coming straight off InvoiceDetail.
  const invoiceDate = fmtDateTime(invoice.invoiceDate);
  const encounterDate = fmtDateTime(invoice.encounterDate ?? null);
  const claimDate = fmtDateTime(
    (claim as { claimDate?: string } | null | undefined)?.claimDate ?? null,
  );
  const eligibilityStart = fmtShortDate(invoice.eligibilityStartDate ?? null);
  const eligibilityEnd = fmtShortDate(invoice.eligibilityEndDate ?? null);

  // Columns follow the grid's own width, not the window's (PROD-3203 M2): the
  // appointment page embeds this grid in a narrow pane on a wide screen, where
  // three columns left each value a few characters before it truncated.
  return (
    <section className="@container rounded-xl border border-border-default bg-surface-card px-6 py-6">
      <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-x-8 gap-y-1">
        {/* Column 1 — Patient */}
        <div className="flex flex-col gap-1">
          <Row label="Patient">
            <span className="text-brand-depth font-medium inline-flex items-center gap-1">
              {patient?.name ?? '—'}
              {patient?.id ? (
                onViewPatient ? (
                  <button
                    type="button"
                    onClick={onViewPatient}
                    aria-label="View patient profile"
                    className="inline-flex text-brand-primary hover:text-interactive-hover cursor-pointer"
                  >
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </button>
                ) : (
                  <ExternalLink className="h-3 w-3 text-brand-primary" aria-hidden />
                )
              ) : null}
            </span>
          </Row>
          <Row label="MRN">{mrnDisplay}</Row>
          <Row label="Age / gender">{ageGender}</Row>
          <Row label="Address">
            {patientAddress}
          </Row>
        </div>

        {/* Column 2 — Encounter / Invoice (+ Claim rows on the claim variant) */}
        <div className="flex flex-col gap-1">
          <Row label="Encounter date">{encounterDate ?? '—'}</Row>
          <Row label="Invoice #">{invoice.invoiceNumber ?? '—'}</Row>
          <Row label="Invoice date">{invoiceDate ?? '—'}</Row>
          {!isInvoice && (
            <>
              <Row label="Claim ID">
                {(claim as { claimId?: string } | null | undefined)?.claimId ?? '—'}
              </Row>
              <Row label="Claim date">{claimDate ?? '—'}</Row>
            </>
          )}
          <Row label={isInvoice ? 'Practitioner' : 'Provider'}>
            {provider?.name ?? '—'}
          </Row>
          <Row label="Encounter #">{invoice.encounterNumber ?? '—'}</Row>
        </div>

        {/* Column 3 — status + sponsor / insurance */}
        <div className="flex flex-col gap-1">
          {isInvoice ? (
            <Row label="Invoice status">{invoice.invoiceStatus ?? '—'}</Row>
          ) : (
            <Row label="Claim status">{claim?.claimStatus ?? '—'}</Row>
          )}
          <Row label="Sponsor">{invoice.sponsorType ?? '—'}</Row>
          {/* Insurance / Member-ID / Eligibility — claim variant always; invoice
              variant only when NOT Self-pay (matches Flutter ProfileInfoWidget). */}
          {(!isInvoice || !isSelfPay) && (
            <>
              <Row label="Insurance">{sponsor?.payerName ?? sponsor?.name ?? '—'}</Row>
              <Row label="Member ID">{invoice.policyNo ?? '—'}</Row>
              <Row label="Eligibility start date">{eligibilityStart ?? '—'}</Row>
              <Row label="Expiration date">{eligibilityEnd ?? '—'}</Row>
            </>
          )}
          {!isInvoice && <Row label="Phone #">{payerPhone}</Row>}
        </div>
      </div>
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-xs font-medium leading-4 text-text-secondary shrink-0 w-28 text-right">
        {label}
      </div>
      {/* The value wraps, never truncates: an MRN or a date cut to "MRN …" is
          unreadable (PROD-3203 M2). `min-w-0` lets it shrink and wrap in a flex row. */}
      <div className="min-w-0 break-words text-sm leading-5 text-text-primary">
        {children}
      </div>
    </div>
  );
}

function InfoGridSkeleton() {
  return (
    <section className="rounded-xl border border-border-default bg-surface-card px-6 py-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
        {[0, 1, 2].map((col) => (
          <div key={col} className="flex flex-col gap-2">
            {[0, 1, 2, 3, 4, 5].map((row) => (
              <Skeleton key={row} className="h-4 w-full" />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/** "May 14, 2026 9:37p" — Flutter parity for date+time fields. */
function fmtDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = parseISO(value);
  if (!isDateValid(parsed)) return value;
  return formatDate(parsed, 'MMM d, yyyy h:mma').replace(/AM$/, 'a').replace(/PM$/, 'p');
}

/** "May 14, 2026" — short-date Flutter parity. */
function fmtShortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = parseISO(value);
  if (!isDateValid(parsed)) return value;
  return formatDate(parsed, 'MMM d, yyyy');
}
